import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { MaintenanceFormData, MaintenanceMode } from './types';

/**
 * The record-specific field set: type, file upload, date, description, and the
 * optional "set up next maintenance reminder" sub-form. Ported field-for-field
 * (labels, options, validation messages) from Add/EditMaintenanceRecordModal.
 * The three legacy `screenView` copies collapse into responsive classes
 * (mobile base, `md:` ≥600px covers tablet+desktop, which were identical).
 */
export function RecordFields({
  form,
  mode,
  fileName,
  handleFileChange,
}: {
  form: UseFormReturn<MaintenanceFormData>;
  mode: MaintenanceMode;
  fileName: string | null;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = form;
  const setReminder = watch('setReminder');
  const reminderType = watch('reminderType');

  return (
    <>
      {/* Maintenance Type Dropdown */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Type
        </label>
        <select
          {...register('maintenanceType', { required: 'Type is required' })}
          className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
        >
          <option value="" disabled>
            Choose a Type
          </option>
          <option value="repairs">Repairs</option>
          <option value="tires">Tires</option>
          <option value="preventative">Preventative</option>
        </select>
        {errors.maintenanceType && (
          <p className="mt-1 text-sm text-red-500">
            {errors.maintenanceType.message}
          </p>
        )}
      </div>

      {/* File Upload */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Upload File
        </label>
        <div className="max-h-[100px] w-full overflow-x-hidden rounded-md border-2 border-dashed border-gray-300 p-2 text-center md:max-h-none md:overflow-x-visible md:p-4">
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.heic,.heif"
            {...register('file', {
              required:
                mode === 'create'
                  ? 'File is required'
                  : fileName === null
                    ? 'File is required'
                    : false,
              validate: {
                validateFileSize: (files) => {
                  const f = files?.[0];
                  return f
                    ? f.size <= 15 * 1024 * 1024 || 'File exceeds 15MB'
                    : true;
                },
              },
            })}
            id="file-upload"
            className="hidden"
            onChange={handleFileChange}
          />
          <label
            htmlFor="file-upload"
            className="cursor-pointer overflow-hidden text-blue-500"
          >
            {fileName
              ? fileName
              : 'Drag a file here or browse to choose a file'}
          </label>
          <p className="mt-1 text-xs text-gray-500">Max 15 MB file</p>
        </div>
        {errors.file && (
          <p className="mt-1 text-sm text-red-500">{errors.file.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Date of Maintenance
        </label>
        <input
          type="date"
          {...register('dateOfMaintenance', {
            required: 'Date of maintenance is required',
          })}
          className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">
          Description of Maintenance
        </label>
        <input
          type="text"
          {...register('description', {})}
          placeholder="Enter description"
          className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
        />
      </div>

      {/* Set Reminder Toggle */}
      <div className="flex items-center">
        <Controller
          control={control}
          name="setReminder"
          render={({ field: { onChange, onBlur, value, ref, name } }) => (
            <input
              type="checkbox"
              checked={value ?? false}
              onChange={onChange}
              onBlur={onBlur}
              name={name}
              ref={ref}
              className="h-6 w-6 rounded border-gray-300 text-blue-500"
            />
          )}
        />
        <label
          htmlFor="set-reminder"
          className="text-md ml-2 block text-gray-700"
        >
          Set Up Next Maintenance Reminder
        </label>
      </div>
      {setReminder && (
        <div className="flex flex-col gap-1 md:gap-3">
          <div>
            <p className="text-sm font-medium text-gray-700">Remind at</p>
            <div className="flex space-x-2 md:space-x-4">
              <label className="flex items-center text-sm md:text-base">
                <input
                  {...register('reminderType', {
                    required: 'Please choose when to remind you',
                  })}
                  type="radio"
                  value="date"
                  className="mr-2"
                />
                Date
              </label>
              <label className="flex items-center text-sm md:text-base">
                <input
                  {...register('reminderType', {
                    required: 'Please choose when to remind you',
                  })}
                  type="radio"
                  value="days"
                  className="mr-2"
                />
                After # of days
              </label>
              <label className="flex items-center text-sm md:text-base">
                <input
                  {...register('reminderType', {
                    required: 'Please choose when to remind you',
                  })}
                  type="radio"
                  value="mileage"
                  className="mr-2"
                />
                After # of miles
              </label>
            </div>
            {errors.reminderType && (
              <p className="mt-1 text-sm text-red-500">
                {errors.reminderType.message}
              </p>
            )}
          </div>
          {reminderType === 'date' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Date
              </label>
              <input
                type="date"
                {...register('reminderValue', { required: 'Date is required' })}
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
              />
            </div>
          )}
          {reminderType === 'days' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                After # of Days
              </label>
              <input
                type="number"
                {...register('reminderValue', {
                  required: 'Number of days is required',
                })}
                placeholder="Enter number of days"
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
              />
            </div>
          )}
          {reminderType === 'mileage' && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                After # of Miles
              </label>
              <input
                type="number"
                {...register('reminderValue', {
                  required: 'Number of miles is required',
                })}
                placeholder="Enter number of miles"
                className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
              />
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default RecordFields;
